import React, { useState, useEffect, useCallback } from 'react';
import { format, addDays, isSameDay, parseISO } from 'date-fns';
import { ClockIcon, MapPinIcon, CurrencyRupeeIcon } from '@heroicons/react/24/outline';
import availabilityAPI from '../../../../api/availability';
import { formatISTRange } from '../../utils/date';

const SlotPicker = ({ 
  selectedDoctor, 
  selectedDate, 
  onSlotSelect, 
  selectedSlot,
  doctorInfo 
}) => {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [lastLoadedDate, setLastLoadedDate] = useState(null);

  // Load slots for selected date - ONLY when date changes
  useEffect(() => {
    if (selectedDoctor && selectedDate) {
      const dateStr = selectedDate instanceof Date 
        ? format(selectedDate, 'yyyy-MM-dd')
        : selectedDate;
      
      // Only load if we haven't already loaded this date
      if (lastLoadedDate !== dateStr) {
        console.log('Loading slots for new date:', dateStr);
        loadDaySlots();
        setLastLoadedDate(dateStr);
      }
    }
  }, [selectedDoctor, selectedDate]);

  const loadDaySlots = useCallback(async () => {
    if (!selectedDoctor || !selectedDate) return;
    
    try {
      setLoading(true);
      
      // Ensure we have the doctor ID properly
      const doctorId = selectedDoctor._id || selectedDoctor.id || selectedDoctor;
      
      // Ensure date is properly formatted
      const dateStr = selectedDate instanceof Date 
        ? format(selectedDate, 'yyyy-MM-dd')
        : selectedDate;
      
      console.log('Loading slots for doctor:', doctorId, 'date:', dateStr);
      
      // Get detailed slots for the specific date
      const response = await availabilityAPI.getDoctorSlots(doctorId, dateStr);
      console.log('Slots response for', dateStr, ':', response);
      
      if (response.success && response.data) {
        const slots = response.data.data?.slots || response.data?.slots || [];
        console.log('Processed slots:', slots);
        
        // Filter out unavailable slots based on status
        const unavailableStatuses = ['Expired', 'Booked', 'Completed', 'Held', 'Closed', 'Cancelled'];
        
        // Transform slots to include formatted time and a stable id
        const formattedSlots = slots
          .filter(slot => {
            // Filter by status if it exists
            if (slot.status) {
              return !unavailableStatuses.includes(slot.status);
            }
            // Fallback to isAvailable check
            return slot.isAvailable !== false;
          })
          .map(slot => {
            const start = new Date(slot.startTime);
            const end = new Date(slot.endTime);
            const durationMin = Math.max(1, Math.round((end - start) / (60 * 1000)));
            return {
              ...slot,
              id: slot.slotId || slot.id || slot._id || slot.time || `${slot.startTime}-${slot.endTime}`,
              formattedTime: formatISTRange(slot.startTime, slot.endTime),
              isAvailable: slot.isAvailable !== false && (!slot.status || !unavailableStatuses.includes(slot.status)),
              durationMin
            };
          });
        
        console.log('Filtered available slots:', formattedSlots);
        setAvailableSlots(formattedSlots);
      } else {
        console.error('Failed to load slots:', response.message);
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error loading day slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDoctor, selectedDate]);

  const loadUpcomingDates = async () => {
    try {
      // DISABLED to prevent 429 errors - too many API calls
      // Instead, generate mock upcoming dates
      const dates = [];
      const today = new Date();
      
      // Generate next 7 days with mock data
      for (let i = 0; i < 7; i++) {
        const date = addDays(today, i);
        const dateStr = format(date, 'yyyy-MM-dd');
        
        dates.push({
          date,
          dateStr,
          label: format(date, 'EEE, d MMM'),
          availableSlots: Math.floor(Math.random() * 5) + 1 // Mock 1-5 slots
        });
      }
      
      setUpcomingDates(dates);
    } catch (error) {
      console.error('Error loading upcoming dates:', error);
    }
  };

  const generateMockSlots = () => {
    const slots = [];
    const startHour = 9;
    const endHour = 17;
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        // Skip lunch break (1-2 PM)
        if (hour === 13) continue;
        
        const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const endHour24 = minute === 30 ? hour + 1 : hour;
        const endMinute = minute === 30 ? 0 : 30;
        const endTime = `${endHour24.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
        
        // Random availability (70% chance)
        if (Math.random() > 0.3) {
          slots.push({
            id: `slot-${hour}-${minute}`,
            startTime,
            endTime,
            formattedTime: `${format(new Date(`2000-01-01T${startTime}`), 'h:mm a')} - ${format(new Date(`2000-01-01T${endTime}`), 'h:mm a')}`,
            isAvailable: true,
            duration: 30
          });
        }
      }
    }
    
    return slots;
  };

  const handleDateSelect = (dateInfo) => {
    if (dateInfo.availableSlots > 0) {
      // This will trigger the parent to update selectedDate, which will reload slots
      onSlotSelect(null); // Clear selected slot
      // Notify parent about date change - you might need to pass this up
    }
  };

  const fees = doctorInfo?.fees || {
    consultation: 2500,
    repeat: 1700,
    beyond30Days: 2500
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header with Doctor Info */}
      <div className="p-4">

        {/* Date Selection Strip */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-semibold text-theme-heading">Select Date</h3>
          </div>
          <div className="flex gap-2 pb-2 overflow-x-auto">
            {upcomingDates.map((dateInfo) => (
              <button
                key={dateInfo.dateStr}
                onClick={() => handleDateSelect(dateInfo)}
                className={`
                  rounded-lg p-3 text-center cursor-pointer border transition-all flex-shrink-0 min-w-[100px]
                  ${selectedDate && isSameDay(selectedDate, dateInfo.date)
                    ? 'bg-theme-border text-white border-theme-border'
                    : 'bg-white text-theme-heading border-gray-200 hover:border-theme-border'
                  }
                  ${dateInfo.availableSlots === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                disabled={dateInfo.availableSlots === 0}
              >
                <div className="font-medium text-xs mb-1">{dateInfo.label}</div>
                <div className={`text-[10px] px-2 py-0.5 rounded-full ${
                  selectedDate && isSameDay(selectedDate, dateInfo.date) 
                    ? 'bg-white/20 text-white' 
                    : dateInfo.availableSlots > 0 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-600'
                }`}>
                  {dateInfo.availableSlots > 0 
                    ? `${dateInfo.availableSlots} Available` 
                    : 'Fully Booked'
                  }
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Slot Selection */}
        {selectedDate && (
          <div className="mb-4">
            <h3 className="text-base font-semibold text-theme-heading mb-3 flex items-center">
              <ClockIcon className="w-4 h-4 mr-2 text-theme-border" />
              Available Time Slots
            </h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-border"></div>
                <span className="ml-3 text-theme-text text-sm">Loading slots...</span>
              </div>
            ) : availableSlots.length > 0 ? (
              <div
                className="grid grid-cols-2 gap-3"
                role="listbox"
                aria-label="Available time slots"
              >
                {availableSlots.map((slot) => (
                  <button
                    key={slot.id}
                    role="option"
                    aria-selected={selectedSlot?.id === slot.id}
                    className={`
                      rounded-lg px-4 py-3 text-center border transition-all duration-200 w-full
                      ${selectedSlot?.id === slot.id
                        ? 'bg-theme-border text-white border-theme-border shadow-md'
                        : 'bg-theme-blue/20 text-theme-border border-theme-border/30 hover:border-theme-border hover:shadow-sm'
                      }
                    `}
                    onClick={() => onSlotSelect(slot)}
                  >
                    <div className="font-medium text-sm whitespace-nowrap">{slot.formattedTime}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClockIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <h3 className="text-sm font-semibold text-theme-heading mb-1">No slots available</h3>
                <p className="text-theme-text text-xs">Please try selecting a different date</p>
              </div>
            )}
          </div>
        )}

        {/* Selection Summary & Continue Button */}
        {selectedSlot && (
          <div className="bg-theme-blue/20 rounded-xl p-4 border border-theme-border/30">
            <div className="text-center mb-3">
              <h4 className="text-sm font-semibold text-theme-heading mb-2">Appointment Summary</h4>
              <div className="text-xs text-theme-text space-y-1">
                <p>
                  <span className="font-medium">Date:</span>{' '}
                  <span className="text-theme-border font-semibold">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                </p>
                <p>
                  <span className="font-medium">Time:</span>{' '}
                  <span className="text-theme-border font-semibold">{selectedSlot.formattedTime}</span>
                </p>
                <p>
                  <span className="font-medium">Fees:</span>{' '}
                  <span className="text-theme-border font-semibold">₹{fees.consultation}</span>
                </p>
              </div>
            </div>
            <button
              className="w-full bg-theme-border hover:bg-theme-border/90 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 text-sm"
              onClick={() => {
                // This would typically navigate to the booking form
                console.log('Proceed to booking form');
              }}
            >
              Continue to Book Appointment
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlotPicker;
